<template>
    <div>
        <b-table :items="items" :fields="kkmFields" striped small responsive show-empty @row-clicked="toggleKkmDetails" class="table-borderless">
            <template slot="action" slot-scope="row">
                <b-dropdown v-if="!isTaxpayer" variant="link" style="position: static;">
                    <template slot="button-content">
                        <dots-icon title="" />
                    </template>
                    <b-dd-item v-if="isOperator" @click.stop="openEditKkmModal(row)" :disabled="row.item.programTask || !row.item.programMode">
                        {{ $t('general.edit') }}
                    </b-dd-item>
                    <b-dd-item v-if="row.item.status === 'CREATED' || row.item.status === 'STOP'"
                        @click.stop="openStartFiscalModal(row)" :disabled="row.item.programTask || !row.item.programMode">
                        {{ $t('kkm.register') }}
                    </b-dd-item>
                    <b-dd-item v-else @click.stop="openStopFiscalModal(row)" :disabled="row.item.programTask || !row.item.programMode">
                        {{ $t('kkm.unregister') }}
                    </b-dd-item>
                    <b-dd-item v-if="isOperator" @click.stop="openSetBlockStatusModal(row)">
                        {{ $t('menu.setBlockStatus') }}
                    </b-dd-item>
                    <b-dd-item @click.stop="openSetOfdDataModal(row)" :disabled="row.item.programTask || !row.item.programMode">
                        {{ $t('menu.setOfdData') }}
                    </b-dd-item>
                    <b-dd-item @click.stop="openSetModeModal(row)" :disabled="row.item.programTask">
                        {{ $t('menu.setMode') }}
                    </b-dd-item>
                    <b-dd-item v-if="isOperator" @click.stop="openSetCashierModal(row)" :disabled="row.item.programTask || !row.item.programMode">
                        {{ $t('menu.setCashier') }}
                    </b-dd-item>
                    <b-dd-item v-if="isOperator" @click.stop="openSetPointModal(row)" :disabled="row.item.programTask || !row.item.programMode">
                        {{ $t('menu.setPoint') }}
                    </b-dd-item>
                    <b-dd-item v-if="isOperator" @click.stop="openSetCanChangePublicKeyModal(row)">
                        {{ $t('menu.setCanChangePublicKey') }}
                    </b-dd-item>
                    <b-dd-item v-if="canCloseCcm(row.item)" @click.stop="openCloseCcmModal(row)">
                        {{$t('menu.closeCcm')}}
                    </b-dd-item>
                    <b-dd-item @click.stop="openCreateMpModal(row)">
                        {{ $t('menu.createMoneyPlacement') }}
                    </b-dd-item>
                    <b-dd-item @click.stop="goToAttributesPage(row.item)">
                        {{ $t('menu.attributes') }}
                    </b-dd-item>
                </b-dropdown>
                <b-dropdown v-else variant="link" style="position: static;">
                    <template slot="button-content">
                        <dots-icon title="" />
                    </template>
                    <b-dd-item @click.stop="openSetOfdDataModal(row)" :disabled="row.item.programTask || !row.item.programMode">
                        {{ $t('menu.setOfdData') }}
                    </b-dd-item>
                    <b-dd-item @click.stop="openSetModeModal(row)" :disabled="row.item.programTask">
                        {{ $t('menu.setMode') }}
                    </b-dd-item>
                    <b-dd-item @click.stop="openCreateMpModal(row)">
                        {{ $t('menu.createMoneyPlacement') }}
                    </b-dd-item>
                    <b-dd-item @click.stop="goToAttributesPage(row.item)">
                        {{ $t('menu.attributes') }}
                    </b-dd-item>
                </b-dropdown>
            </template>
            <template slot="status" slot-scope="row">
                <span v-b-tooltip.hover.bottom :title="row.item.status != null ? $t(`kkm.statusFullName.${row.item.status || ''}`) : ''">
                    {{ row.item.status != null ? $t(`kkm.statusShortName.${row.item.status || ''}`) : ''}}
                </span>
            </template>
            <template slot="ofdStatus" slot-scope="row">
                <span v-b-tooltip.hover.bottom :title="row.item.ofdStatus != null ? $t(`kkm.statusOfdFullName.${row.item.ofdStatus || ''}`) : ''">
                    {{ row.item.ofdStatus != null ? $t(`kkm.statusOfdShortName.${row.item.ofdStatus || ''}`) : ''}}
                </span>
            </template>
            <template slot="clientStatus" slot-scope="row">
                <span v-b-tooltip.hover.bottom :title="row.item.clientStatus != null ? $t('kkm.clientStatus') : ''">
                    {{ row.item.clientStatus != null ? row.item.clientStatus : ''}}
                </span>
            </template>
            <template slot="clientStatusMessage" slot-scope="row">
                <span v-b-tooltip.hover.bottom :title="row.item.clientStatusMessage != null ? $t('kkm.clientStatusMessage') : ''">
                    {{ row.item.clientStatusMessage != null ? row.item.clientStatusMessage : ''}}
                </span>
            </template>
            <template slot="clientStatusDate" slot-scope="row">
                <span v-b-tooltip.hover.bottom :title="row.item.clientStatusMessage != null ? $t('kkm.clientStatusDate') : ''">
                    {{ row.item.clientStatusDate != null ? $d(new Date(row.item.clientStatusDate), 'long') : ''}}
                </span>
            </template>
            <template slot="mode" slot-scope="row">
                <span v-b-tooltip.hover.bottom :title="row.item.programMode ? $t(`kkm.programModeUserLabel`) + ': ' + row.item.programUser : null">
                    {{ row.item.programTask ? $t(`kkm.programTask`) : ( row.item.programMode ? $t(`kkm.programMode`) : $t(`kkm.workMode`)) }}
                </span>
            </template>
            <template slot="openingDate" slot-scope="row">
                {{ row.item.openingDate == null ? '-' : $d(row.item.openingDate, 'short') }}
            </template>
            <template slot="row-details" slot-scope="row">
                <kkm-details :row="row"></kkm-details>
            </template>
            <template slot="empty">
                <div class="text-center">
                    {{ $t('general.noData') }}
                </div>
            </template>
        </b-table>
        <b-modal ref="kkmStartFiscalModal" :title="$t('menu.startFiscalization')" size="lg" hide-footer lazy>
            <kkm-start-fiscalization-form :sfd-num="initData.sfdNumber" :start-fiscal-response="onEditKkmResponse"
                :is-operator="isOperator" />
        </b-modal>
        <b-modal ref="kkmStopFiscalModal" :title="$t('menu.stopFiscalization')" size="lg" hide-footer lazy>
            <kkm-stop-fiscalization-form :sfd-num="initData.sfdNumber" :stop-fiscal-response="onEditKkmResponse"
                :is-operator="isOperator" />
        </b-modal>
        <b-modal ref="kkmSetOfdDataModal" :title="$t('menu.setOfdData')" hide-footer lazy>
            <kkm-set-ofd-data-form :sfd-num="initData.sfdNumber" :ofd-connection-string="initData.ofdConnectionString"/>
        </b-modal>
        <b-modal ref="setOfdWorkModal" :title="$t('menu.setOfdWork')"
            @ok="setOfdWork" :cancel-title="$t('general.cancel')" lazy>
            <p>{{ $t('kkm.setOfdWorkConfirmation', [initData.sfdNumber]) }}</p>
        </b-modal>
        <b-modal ref="ofdWorkSetModal" :title="$t('menu.setOfdWork')" ok-only lazy>
            <b-alert variant="success" show>{{ $t('kkm.ofdWorkSetMessage') }}</b-alert>
        </b-modal>
        <b-modal ref="setOfdWorkErrorModal" :title="$t('menu.setOfdWork')" ok-only lazy>
            <b-alert variant="danger" show>{{ alert }}</b-alert>
        </b-modal>
        <b-modal ref="setBlockStatusModal" :title="$t('menu.setBlockStatus')" hide-footer lazy>
            <kkm-set-block-status-form :kkm-id="initData.id" :blocked="initData.blocked" />
        </b-modal>
        <b-modal ref="setModeModal" :title="$t('menu.setMode')" hide-footer lazy>
            <kkm-set-mode-form :kkm-id="initData.id" :program-mode="initData.programMode"/>
        </b-modal>
        <b-modal ref="setPointModal" :title="$t('menu.setPoint')" hide-footer lazy>
            <kkm-set-point-form :kkm-id="initData.id" :point-title="initData.salePointTitle" :point-address="initData.address" :timezone="initData.timezone"/>
        </b-modal>
        <b-modal ref="setCashierModal" :title="$t('menu.setCashier')" hide-footer lazy>
            <kkm-set-cashier-form :kkm-id="initData.id" />
        </b-modal>
        <b-modal ref="setCanChangePublicKeyModal" :title="$t('menu.setCanChangePublicKey')" hide-footer lazy>
            <kkm-set-can-change-public-key :kkm-id="initData.id" :close-modal="closeSetCanChangePublicKeyModal" />
        </b-modal>
        <b-modal ref="editKkmModal" :title="$t('general.edit')" hide-footer lazy>
            <kkm-edit-form :init-data="initData" />
        </b-modal>
        <b-modal ref="closeCcmModal" :title="$t('menu.closeCcm')" hide-footer lazy>
            <kkm-close :ccm-sfd-number="initData.sfdNumber" :close-modal="closeCloseCcmModal"/>
        </b-modal>
        <b-modal ref="createMpModal" :title="$t('menu.createMoneyPlacement')" hide-footer lazy>
            <create-mp 
                :taxpayer="$route.params" 
                :control-cash-machine="initData" 
                :on-close="closeCreateMpModal"
                :is-modal="true" />
        </b-modal>
    </div>
</template>

<script>
/* eslint-disable */
const KkmDetails = () => import(/* webpackChunkName: "chunk-views" */ './KkmDetailsView.vue')
const KkmSetOfdDataForm = () => import(/* webpackChunkName: "chunk-views" */ './KkmSetOfdDataForm.vue')
const KkmStartFiscalizationForm = () => import(/* webpackChunkName: "chunk-views" */ './KkmStartFiscalizationForm.vue')
const KkmStopFiscalizationForm = () => import(/* webpackChunkName: "chunk-views" */ './KkmStopFiscalizationForm.vue')
const KkmSetPointForm = () => import(/* webpackChunkName: "chunk-views" */ './KkmSetPointForm.vue')
const KkmSetCashierForm = () => import(/* webpackChunkName: "chunk-views" */ './KkmSetCashierForm.vue')
const KkmSetBlockStatusForm = () => import(/* webpackChunkName: "chunk-views" */ './KkmSetBlockStatusForm.vue')
const KkmSetModeForm = () => import(/* webpackChunkName: "chunk-views" */ './KkmSetModeForm.vue')
const KkmSetCanChangePublicKey = () => import(/* webpackChunkName: "chunk-views" */ './KkmSetCanChangePublicKey.vue')
const KkmEditForm = () => import(/* webpackChunkName: "chunk-views" */ './KkmEditForm.vue')
const KkmClose = () => import(/* webpackChunkName: "chunk-views" */ './KkmClose.vue');
const CreateMp = () => import(/* webpackChunkName: "chunk-views" */ '@/components/moneyplacement/MoneyPlacementCreateForm.vue');

export default {
    name: 'kkmListView',
    props: {
        items: Array,
        taxOrgs: Array,
        hideAlert: Function,
        refreshKkmList: Function
    },
    components: {
        KkmDetails,
        KkmSetOfdDataForm,
        KkmStartFiscalizationForm,
        KkmStopFiscalizationForm,
        KkmSetPointForm,
        KkmSetCashierForm,
        KkmSetBlockStatusForm,
        KkmSetModeForm,
        KkmSetCanChangePublicKey,
        KkmEditForm,
        KkmClose,
        CreateMp
    },
    data () {
        return {
            alert: '',
            initData: {}
        }
    },
    computed: {
        isOperator: function () {
            return this.$store.getters.getUserRole === this.$appConfig.role.operator
        },
        isTaxpayer: function () {
            return this.$store.getters.getUserRole === this.$appConfig.role.dealer
        },
        kkmFields: function () {
            return [
                { key: 'sfdNumber', label: 'ID' },
                { key: 'openingDate', label: this.$t('kkm.created') },
                { key: 'taxName', label: this.$t('kkm.inspectionName') },
                { key: 'registrationNumber', label: this.$t('kkm.registration') },
                { key: 'factoryNumber', label: this.$t('kkm.factoryNum') },
                { key: 'status', label: this.$t('kkm.status') },
                { key: 'ofdStatus', label: this.$t('kkm.ofdStatus') },
                { key: 'clientStatus', label: this.$t('kkm.clientStatus') },
                { key: 'clientStatusMessage', label: this.$t('kkm.clientStatusMessage') },
                { key: 'clientStatusDate', label: this.$t('kkm.clientStatusDate') },
                { key: 'mode', label: this.$t('kkm.mode') },
                { key: 'action', label: this.$t('general.actions') }
            ]
        },
        rows() {
            return this.items.map(x => { 
                x._showDetails = false;
                return x;    
            });
        }
    },
    methods: {
        taxOrgName: function (id) {
            if (!this.taxOrgs.length) {
                return id
            }
            var org = this.taxOrgs.find(item => {
                return item.value === id
            })
            return org !== undefined ? org.text : id.toString()
        },
        toggleKkmDetails(item, index, event) {
            item._showDetails = !item._showDetails
        },
        openEditKkmModal(row) {
            this.initializeData(row)
            this.$refs.editKkmModal.show()
        },
        openStopFiscalModal(row) {
            this.initializeData(row)
            this.$refs.kkmStopFiscalModal.show()
        },
        openStartFiscalModal(row) {
            this.initializeData(row)
            this.$refs.kkmStartFiscalModal.show()
        },
        openSetOfdDataModal(row) {
            this.initializeData(row)
            this.$refs.kkmSetOfdDataModal.show()
        },
        openSetBlockStatusModal(row) {
            this.initializeData(row)
            this.$refs.setBlockStatusModal.show()
        },
        openSetModeModal(row) {
            this.initializeData(row)
            this.$refs.setModeModal.show()
        },
        openSetPointModal(row) {
            this.initializeData(row)
            this.$refs.setPointModal.show()
        },
        openSetCashierModal(row) {
            this.initializeData(row)
            this.$refs.setCashierModal.show()
        },
        openSetCanChangePublicKeyModal(row) {
            this.initializeData(row)
            this.$refs.setCanChangePublicKeyModal.show()
        },
        closeSetCanChangePublicKeyModal(row) {
            this.$refs.setCanChangePublicKeyModal.hide()
        },
        openCloseCcmModal(row) {
            this.initializeData(row);
            this.$refs.closeCcmModal.show();
        },
        openCreateMpModal(row) {
            this.initializeData(row);
            this.$refs.createMpModal.show();
        },
        closeCreateMpModal() {
            this.$refs.createMpModal.hide();
        },
        closeCloseCcmModal(row) {
            this.$refs.closeCcmModal.hide();
        },
        initializeData(row) {
            this.hideAlert()
            this.initData = row.item
        },
        onEditKkmResponse(result) {
            this.refreshKkmList({ dealerId: this.$route.params.id })
            this.$refs.kkmStartFiscalModal.hide()
            this.$refs.kkmStopFiscalModal.hide()
        },
        setOfdWork(row) {
            const onSuccess = response => {
                this.refreshKkmList({ factoryNumber: this.initData.factoryNumber });
                this.$refs.ofdWorkSetModal.show();
            };
            
            const onError = error => {
                this.alert = this.$t(`errors.${error}`);
                this.$refs.setOfdWorkErrorModal.show();
            }; 

            this.$request.sendRequest(
                this, 
                this.$appConfig.apiUrls.kkm.setOfdWork,
                { sfdNumber: this.initData.sfdNumber },
                onSuccess,
                onError);
        },
        canCloseCcm(ccm) {
            return ccm != null && this.isOperator && ccm.programMode && ccm.status == 'STOP';
        },
        goToAttributesPage(kkm) {
            this.$router.push({name: 'kkmAttributes', params: { kkmId: kkm.id }});
        }
    }
}
</script>
